import { mapRoomAuthoredData } from './mapRoomAuthoredData.js';
import { productionSetups } from './productionSetups.js';

const actors = { NORA: 'nora', ORTIZ: 'ortiz', EVELYN: 'evelyn', VOSS: 'voss' };
const voices = { nora: 'bm_fable', ortiz: 'af_bella', evelyn: 'bm_fable', voss: 'am_adam' };
const allActors = Object.values(actors);
const normalizedText = text => text?.replaceAll('’', "'");
// These two author-approved adaptations are recorded in the durable final QA.
// They retain the earlier cue IDs, while the original edition stays unchanged.
const adaptedAnchors = { 24: 'murder-c05', 120: 'murder-c13' };

export function buildExpandedMapRoom(base) {
  if (base.id !== 'map-room-murder') throw new Error('Expected the existing Map Room scenario.');
  const result = structuredClone(base), usedAnchors = new Set();
  result.catalogRevision = 2;
  result.duration = 'Full story · 590 cues';
  result.scenes = mapRoomAuthoredData.scenes.map(({ firstCue, lastCue, ...scene }) => ({
    ...scene, tone: 'Evidence, uncertainty, and an earned decision',
  }));
  result.cues = mapRoomAuthoredData.cues.map(authored => {
    const characterId = actors[authored.actor], ending = authored.actor === 'SELECTED ENDING | PERFORMANCE BEAT';
    const matches = base.cues.filter(cue => adaptedAnchors[authored.number]
      ? cue.id === adaptedAnchors[authored.number]
      : ending ? cue.type === 'ending' && cue.text === authored.endingText
      : authored.dialogueText && cue.characterId === characterId
        && normalizedText(cue.dialogueText) === normalizedText(authored.dialogueText));
    if (matches.length > 1) throw new Error('Ambiguous Map Room source anchor.');
    const anchor = matches[0];
    if (anchor) usedAnchors.add(anchor.id);
    const common = {
      id: anchor?.id || `map-c${String(authored.number).padStart(3, '0')}`,
      sceneId: authored.sceneId, label: anchor?.label || `Cue ${authored.number} · ${authored.actor}`,
      directionText: authored.directionText, spokenDirectionText: authored.spokenDirectionText, requireAck: true,
    };
    if (characterId) return {
      ...common, visibility: 'character', characterId,
      ...(authored.dialogueText ? { type: 'dialogue', text: authored.dialogueText,
        dialogueText: authored.dialogueText, cueVoice: true }
        : { type: 'action', text: authored.directionText, directorVoice: true, completionMode: 'director' }),
    };
    if (!ending && authored.actor !== 'BOTH | PERFORMANCE BEAT') throw new Error('Unknown Map Room actor.');
    // The four-character manuscript uses BOTH for shared environmental beats.
    // Its selected ending explicitly excludes the detained Marcus Voss.
    return { ...common, type: ending ? 'ending' : 'action', visibility: 'selected',
      characterIds: ending ? ['nora', 'ortiz', 'evelyn'] : [...allActors],
      text: authored.endingText || authored.directionText, directorVoice: true, completionMode: 'director' };
  });
  if (usedAnchors.size !== base.cues.length) throw new Error('Map Room expansion lost a reviewed source anchor.');
  result.characterVoices = { version: 1, assignments: Object.fromEntries(allActors.map(id => [
    id, { provider: 'voicebox', nativeVoiceId: voices[id], source: 'manual' },
  ])) };
  delete result.actorBriefs;
  result.script = result.cues.map(cue => ({ type: cue.type === 'dialogue' ? 'dialogue' : 'direction',
    sceneId: cue.sceneId, characterId: cue.characterId,
    speaker: result.characters.find(character => character.id === cue.characterId)?.name,
    text: cue.dialogueText || cue.text,
  }));
  result.originalScript = [result.title, ...result.scenes.flatMap(scene => [
    `SCENE ${scene.number}: ${scene.title}`,
    ...mapRoomAuthoredData.cues.filter(cue => cue.sceneId === scene.id).map(cue => [
      `Cue ${String(cue.number).padStart(3, '0')} | ${cue.actor}`,
      `Private direction: ${cue.directionText}`, `Spoken cue: ${cue.spokenDirectionText}`,
      ...(cue.dialogueText ? [`Dialogue: ${cue.dialogueText}`] : []),
      ...(cue.endingText ? [`Text: ${cue.endingText}`] : []),
    ].join('\n')),
  ])].join('\n\n');
  return result;
}

export const expandedMapRoomProductionSetup = {
  ...structuredClone(productionSetups['map-room-murder']),
  setupVersion: 'v2', introAudio: false,
  scenes: mapRoomAuthoredData.scenes.map(scene => ({
    setting: scene.setting, props: ['door', 'key', 'coat', 'bag', 'ledger', 'clock', 'letter'],
    staging: 'Use pretend evidence and a clear doorway. Keep items in place until private instructions call for them; mime examination and detention.',
    intro: `You are at ${scene.setting}. Listen for your private instructions.`,
  })),
};
